'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { LockIcon as IconLock } from 'lucide-react';

import Button from '@/components/ui/Button/Button';
import TextInput from '@/components/ui/TextInput/TextInput';
import ToggleButton from '@/components/ui/ToggleButton/ToggleButton';
import useCreateNookMutation from '@/hooks/mutations/useCreateNookMutation';
import { parseError } from '@/lib/errorHandlers';
import { nookIdSchema, passwordSchema } from '@/lib/schemas';
import showToast from '@/lib/showToast';

const createFormSchema = z
  .object({
    name: nookIdSchema,
    isPrivate: z.boolean().default(false),
    password: passwordSchema.optional(),
  })
  .refine(data => !data.isPrivate || (data.isPrivate && data.password), {
    message: 'A password is required for private nooks.',
    path: ['password'],
  });

type CreateFormSchema = z.infer<typeof createFormSchema>;

interface CreateNookFormProps {
  presetId?: string;
}

const CreateNookForm = ({ presetId }: CreateNookFormProps) => {
  const router = useRouter();
  const form = useForm<CreateFormSchema>({
    ...(presetId ? { defaultValues: { name: presetId } } : {}),
    resolver: zodResolver(createFormSchema),
  });

  const createNookMutation = useCreateNookMutation();

  const isPrivate = form.watch('isPrivate');

  const onSubmit = async (data: CreateFormSchema) => {
    try {
      const response = await createNookMutation.mutateAsync(data);
      const { nookId } = response.data;
      showToast(response.message, 'success');
      router.push(`/${nookId}`);
    } catch (error) {
      const err = parseError(error);
      form.setError(err.status === 409 ? 'name' : 'root', {
        message: err.message,
      });
    }
  };

  return (
    <form
      className='flex flex-col gap-4'
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <TextInput<CreateFormSchema>
        aria-label='Nook Name'
        label={
          presetId ? 'Your nook name will be' : 'Enter a name for your nook'
        }
        control={form.control}
        disabled={presetId ? true : false}
        name='name'
        placeholder='my-first-nook'
      />
      {isPrivate && (
        <TextInput<CreateFormSchema>
          aria-label='Nook Password'
          label='Enter a password for your private nook'
          type='password'
          control={form.control}
          name='password'
          placeholder='********'
          icon={<IconLock size={18} />}
        />
      )}
      <p className='fieldset-label text-red-500 font-semibold'>
        {form.formState.errors.root?.message ?? ''}
      </p>
      <ToggleButton
        name='isPrivate'
        label='Make this a private nook'
        control={form.control}
      />
      <Button
        isLoading={createNookMutation.isPending}
        disabled={createNookMutation.isPending}
      >
        Submit
      </Button>
    </form>
  );
};

export default CreateNookForm;
